import { fetchSellersIfNeeded, getHousesById } from '@/redux/modules/seller';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { isArchivedSearch, parseQueryParams } from '@/utils/queryParams';
import { setSearchExclusions } from '@/redux/modules/search/exclusions/searchExclusions.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import FacetTypes from '@/types/search/enums/facetTypes';

type ExcludedHouse = {
    auctionHouseName: string;
    sellerId: number;
};

type HousesToExcludeData = {
    auctionHouseName: string | null;
    selected?: boolean;
    sellerId: string | null;
    totalResults: number | null;
};

type UseExcludeHousesReturn = {
    excludedHouseIds: number[];
    handleApplyExcludeHouses: () => Promise<void>;
    handleRemoveHouse: (sellerId: number) => void;
    handleSelectHouse: (sellerId: string) => void;
    hasExcludedHousesChanges: boolean;
    hasHousesToExcludeChanges: boolean;
    housesById: ReturnType<typeof getHousesById>;
    housesToExcludeData: HousesToExcludeData[];
    localExcludedHouses: ExcludedHouse[];
    selectedHousesToExclude: string[];
};

export const useExcludeHouses = (): UseExcludeHousesReturn => {
    const dispatch = useAppDispatch();
    const excludedHouseIds = useAppSelector((state) => getUserExcludedHouses(state));
    const housesById = useAppSelector((state) => getHousesById(state, excludedHouseIds));

    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const isArchivedSearchPage = isArchivedSearch(queryParams, pathname);

    const auctionHouseFacet = useAppSelector((state) =>
        getMergedOptionFacet(state, isArchivedSearchPage, FacetTypes.AUCTION_HOUSE_ID)
    );

    const housesFacets = useMemo(
        () =>
            ((auctionHouseFacet?.categories && auctionHouseFacet?.options) || []).map((option) => ({
                auctionHouseName: option.name || null,
                sellerId: option.id?.toString() || null,
                totalResults: option.count || null,
            })),
        [auctionHouseFacet]
    );

    const [localExcludedHouses, setLocalExcludedHouses] = useState<ExcludedHouse[]>(() =>
        housesById.map((house) => ({
            auctionHouseName: house.name,
            sellerId: house.sellerId,
        }))
    );

    const [selectedHousesToExclude, setSelectedHousesToExclude] = useState<string[]>([]);

    const hasExcludedHousesChanges = useMemo(() => {
        const currentHouseIds = new Set(localExcludedHouses.map((house) => house.sellerId));
        return excludedHouseIds.some((id) => !currentHouseIds.has(id));
    }, [localExcludedHouses, excludedHouseIds]);

    const hasHousesToExcludeChanges = useMemo(() => {
        return selectedHousesToExclude.length > 0;
    }, [selectedHousesToExclude]);

    const housesToExcludeData = useMemo(
        () =>
            housesFacets.map((house) => ({
                ...house,
                selected: selectedHousesToExclude.includes(house.sellerId || ''),
            })),
        [housesFacets, selectedHousesToExclude]
    );

    useEffect(() => {
        if (housesById.length > 0) {
            const newHouses = housesById.map((house) => ({
                auctionHouseName: house.name,
                sellerId: house.sellerId,
            }));
            setLocalExcludedHouses(newHouses);
        }
    }, [housesById]);

    useEffect(() => {
        if (excludedHouseIds.length) {
            dispatch(fetchSellersIfNeeded(excludedHouseIds));
        }
    }, [dispatch, excludedHouseIds]);

    const handleRemoveHouse = useCallback((sellerId: number) => {
        setLocalExcludedHouses((prev) => prev.filter((house) => house.sellerId !== sellerId));
    }, []);

    const handleSelectHouse = useCallback((sellerId: string) => {
        setSelectedHousesToExclude((prev) => {
            const newSelection = prev.includes(sellerId) ? prev.filter((id) => id !== sellerId) : [...prev, sellerId];
            return newSelection;
        });
    }, []);

    const handleApplyExcludeHouses = useCallback(async () => {
        const currentHouseIds = localExcludedHouses.map((house) => Number(house.sellerId));
        const housesToInclude = excludedHouseIds.filter((id) => !currentHouseIds.includes(id));

        await dispatch(
            setSearchExclusions({
                excluded: selectedHousesToExclude.length > 0 ? selectedHousesToExclude.map(Number) : undefined,
                facetId: 'auctionHouse',
                included: housesToInclude.length > 0 ? housesToInclude : undefined,
            })
        );
    }, [dispatch, excludedHouseIds, localExcludedHouses, selectedHousesToExclude]);

    return {
        excludedHouseIds,
        handleApplyExcludeHouses,
        handleRemoveHouse,
        handleSelectHouse,
        hasExcludedHousesChanges,
        hasHousesToExcludeChanges,
        housesById,
        housesToExcludeData,
        localExcludedHouses,
        selectedHousesToExclude,
    };
};
